import React, { useState, useEffect, useCallback } from "react";
import Navigation from "../../../App/Navigation";
import { ROUTES } from "../../../../consts";
import { SampleVariantsBlock } from "../../../common/ProductSectionAndDate/SampleVariantBlock";
import { useTrackContext } from "../../../../hooks/useTrackContext";
import { ShoppingCartIcon } from "@heroicons/react/outline";
import TrackTile from "../../../TrackSelection/TrackSelectionTile";
import { ICartItemHeadless, IGVariant, TGameOptions, TSelectedVariants } from "../../../../types";
import ProgressBar from "../../../ProgressiveBar";
import { getAdminIdFromStoreFrontId } from "../../../../helpers/shopify";
import { ImSpinner6 } from "react-icons/im";
import { ADD_TO_CART_LINES, GET_IMAGE_BY_ID } from "../../../../graphqlQueries";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../../../config";
import { formatDateWithSuffix } from "../../../../helpers/utils";
import MiniCart from "../../../Cart/MiniCart";
import UpsellModal from "../../../upsellModal";
import { query } from "../../../../lib";

interface UniqueVariant {
  variantSelectedTime: string;
  uniqueVariants: IGVariant[];
}

const VRReviewExperience: React.FC = () => {
  const {
    vrSelection: {
      playersandsessions,
      isTermsAndConditionExcepted,
      variantsAndDate: {
        selectedVariants, //selectedVariantsForUpsell
      },
    },
    // termsAndConditionsContent,
    dispatch,
    productRenderedLocation,
    headlessCartInfo,
    trackSelectionOption,
    virtualRealityUpsellImageIds,
    headlessCart,
    windowWidth,
    metaObjectTrackSelectionConfig,
  } = useTrackContext();

  const { numberOfPlayers, numberOfSessions } = playersandsessions;

  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [isProductAdding, setIsProductAdding] = useState<boolean>(false);
  const [isBookingNow, setIsBookingNow] = useState<boolean>(false);
  const [uniqueVariants, setUniqueVariants] = useState<UniqueVariant[]>([]);
  const [nextRoute, setNextRoute] = useState<string>("#");
  const [isDuplicateModal, setIsDuplicateModal] = useState<boolean>(false);
  const [duplicateVariants, setDuplicateVariants] = useState<TSelectedVariants[]>([]);

  useEffect(() => {
    const uniqueVariantsByTime: { [time: string]: UniqueVariant } = {};

    selectedVariants.forEach((variant) => {
      const variantSelectedTime = variant.variantSelectedTime.toString();

      if (!uniqueVariantsByTime[variantSelectedTime]) {
        uniqueVariantsByTime[variantSelectedTime] = {
          variantSelectedTime,
          uniqueVariants: [],
        };
      }

      (uniqueVariantsByTime[variantSelectedTime].uniqueVariants as IGVariant[]).push(variant.variantsSelectedObject as unknown as IGVariant);
    });

    const uniqueVariantsArray: UniqueVariant[] = Object.values(uniqueVariantsByTime);
    setUniqueVariants(uniqueVariantsArray);
  }, [selectedVariants]);

  const handleAddToCartClick = useCallback(async () => {
    try {
      setIsProductAdding(true);
      if (!selectedVariants || selectedVariants.length === 0) {
        alert("Need to select a time");
      } else {
        if (productRenderedLocation === "external") {
          // Check for duplicates against current cart items
          const cartVariantIds = headlessCart?.map((item) => item.merchandise.id) || [];
          // Find variants that are already in the cart
          const duplicates = selectedVariants.filter((variant) => cartVariantIds.includes(variant.variantsSelectedObject.id));

          if (duplicates.length > 0) {
            setDuplicateVariants(duplicates);
            setIsDuplicateModal(true);
            return;
          }

          const lines = {
            items: selectedVariants.map((variant) => ({
              merchandiseId: variant.variantsSelectedObject.id,
              quantity: numberOfPlayers,
            })),
          };

          const addToCart = await query(ADD_TO_CART_LINES, {
            cartId: headlessCartInfo.id,
            lines: lines.items,
          });

          const isAddedToCart = addToCart?.data?.cartLinesAdd;
          if (isAddedToCart) {
            const cartItems = isAddedToCart?.cart?.lines?.edges?.map((edge: { node: any }) => edge.node);
            dispatch({
              type: "SET_CARTITEMS_HEADLESS",
              payload: {
                headlessCart: [...cartItems] as ICartItemHeadless[],
              },
            });

            const cartTotalAmount = isAddedToCart?.cart?.cost?.totalAmount?.amount;
            dispatch({
              type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
              payload: {
                headlessCartTotalAmount: cartTotalAmount,
              },
            });
            dispatch({
              type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
              payload: {
                isHeadlessMiniCartIsShowing: true,
              },
            });

            setTimeout(() => {
              // setShowUpSellModal(true);
              dispatch({
                type: "SET_HEADLESS_IS_MINI_CART_SHOWING",
                payload: {
                  isHeadlessMiniCartIsShowing: false,
                },
              });
            }, 3000);
          } else {
            console.error("isAddedToCart Cart data is missing in the GraphQL response.", isAddedToCart);
          }
        } else {
          let formData = {
            items: selectedVariants.map((variant) => ({
              id: getAdminIdFromStoreFrontId(variant.variantsSelectedObject.id),
              quantity: numberOfPlayers,
            })),
          };

          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          });
          const cart = await response.json();
          if (cart) {
            miniCartCallback(true);
          }
          setTimeout(() => {
            // setShowUpSellModal(true);
            miniCartCallback(true);
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsProductAdding(false);
    }
  }, [dispatch, headlessCartInfo, numberOfPlayers, productRenderedLocation, selectedVariants, headlessCart]);

  const handleBookNow = useCallback(async () => {
    try {
      setIsBookingNow(true);
      await handleAddToCartClick();
      if (productRenderedLocation === "external") {
        if (headlessCartInfo.checkoutUrl) {
          window.open(headlessCartInfo.checkoutUrl, "_blank");
        }
      } else {
        window.location.href = "/checkout";
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsBookingNow(false);
    }
  }, [headlessCartInfo, handleAddToCartClick, productRenderedLocation]);

  useEffect(() => {
    let newTotalPrice = 0;

    const uniqueVariantsByTime: { [time: string]: UniqueVariant } = {};

    selectedVariants.forEach((variant) => {
      const variantSelectedTime = variant.variantSelectedTime.toString();
      newTotalPrice += parseFloat(variant.variantsSelectedObject.priceV2.amount);
      if (!uniqueVariantsByTime[variantSelectedTime]) {
        uniqueVariantsByTime[variantSelectedTime] = {
          variantSelectedTime,
          uniqueVariants: [],
        };
      }

      (uniqueVariantsByTime[variantSelectedTime].uniqueVariants as IGVariant[]).push(variant.variantsSelectedObject as unknown as IGVariant);
    });

    setTotalPrice(newTotalPrice);

    const uniqueVariantsArray: UniqueVariant[] = Object.values(uniqueVariantsByTime);

    uniqueVariantsArray.sort((a, b) => {
      const dateA = new Date(a.variantSelectedTime);
      const dateB = new Date(b.variantSelectedTime);

      const monthDiff = dateA.getMonth() - dateB.getMonth();
      if (monthDiff !== 0) {
        return monthDiff;
      }

      return dateA.getDate() - dateB.getDate();
    });

    setUniqueVariants(uniqueVariantsArray);
  }, [productRenderedLocation, selectedVariants]);

  useEffect(() => {
    if (!isTermsAndConditionExcepted) {
      const element = document.getElementById("terms-and-condition-wrapper") as HTMLElement;
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      setNextRoute("#");
    } else {
      setNextRoute("/" + ROUTES.VIRTUAL_REALITY.ReviewCart);
    }
    const buttonElement = document.getElementById("terms-and-conditions");
    if (nextRoute === "#") {
      if (buttonElement) {
        buttonElement.classList.add("tw-animate-bounce");
      }
    } else {
      if (buttonElement) {
        buttonElement.classList.remove("tw-animate-bounce");
      }
    }
  }, [isTermsAndConditionExcepted, nextRoute, productRenderedLocation]);

  // Get the upsell virtual reality image data by image graphql id from metaobject
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (virtualRealityUpsellImageIds && virtualRealityUpsellImageIds.length) {
          let imageData: Array<{ url: string; altText: string }> = [];
          for (let imageId of virtualRealityUpsellImageIds) {
            const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
              },
              body: JSON.stringify({
                query: GET_IMAGE_BY_ID,
                variables: {
                  id: imageId as string,
                },
              }),
            });

            if (!response.ok) {
              throw new Error("Network response was not ok");
            }

            const data = await response.json();
            const {
              data: {
                node: { image },
              },
            } = data;
            if (image) {
              imageData.push(image);
            }
          }
          if (imageData && imageData.length) {
            dispatch({
              type: "SET_VIRTUAL_REALITY_UPSELL_IMAGE_URLS",
              payload: {
                virtualRealityUpsellImageUrls: imageData,
              },
            });
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch, virtualRealityUpsellImageIds]);

  // const minitrackUpsellImage = Object.assign(
  //   {},
  //   virtualRealityUpsellImageUrls.find((i: TUpsellImage) => i.altText.toLowerCase().includes("mini track"))
  // ) as TUpsellImage;
  // const mainTrackUpsellImage = Object.assign(
  //   {},
  //   virtualRealityUpsellImageUrls.find((i: TUpsellImage) => i.altText.toLowerCase().includes("main track"))
  // ) as TUpsellImage;
  // const arcadeCardUpsellImage = Object.assign(
  //   {},
  //   virtualRealityUpsellImageUrls.find((i: TUpsellImage) => i.altText.toLowerCase().includes("arcade cards"))
  // ) as TUpsellImage;

  return (
    <>
      <ProgressBar maxValue={100} currentValue={windowWidth <= 768 ? 90 : 75} />
      {productRenderedLocation === "external" && <MiniCart />}

      {isDuplicateModal && duplicateVariants.length > 0 && (
        <UpsellModal
          modalTitle="You've already added this to your cart, are you sure you want to add it again?"
          toggleModalDisplay={() => setIsDuplicateModal(false)}
          isModalOpen={isDuplicateModal}
          trackOptions={trackSelectionOption}
          upsellVariants={duplicateVariants}
          numberOfSeats={numberOfPlayers}
          numberOfRaces={numberOfSessions}
          productRenderedLocation={productRenderedLocation}
          headlessCartInfo={headlessCartInfo}
          isDuplicateModal={duplicateVariants.length > 0}
        />
      )}

      <div className="tw-block lg:tw-hidden">
        <span className="tw-block tw-text-[18px] lg:tw-text-[24px] tw-text-[#9D62FE] tw-font-[700] tw-mt-[30px] tw-text-center">Virtual Reality</span>
        <div className="tw-text-white tw-text-center tw-text-[24px] tw-font-extrabold">Review your Experience</div>
      </div>
      <div className="tw-hidden lg:tw-block">
        <Navigation
          previous={{
            active: true,
            to: "/" + ROUTES.VIRTUAL_REALITY.DATE_TIME,
          }}
          next={{ active: nextRoute !== "#", to: nextRoute }}
          title={"Review your Experience"}
          subtitle={"Virtual Reality"}
        />
      </div>
      <div className="tw-flex tw-justify-center tw-mt-[56px]">
        <div className="tw-block lg:tw-flex lg:tw-items-center lg:tw-gap-x-16">
          <TrackTile
            onDoubleClick={() => console.log("Double Click has fired")}
            isSelected={false}
            displayBorders={true}
            title={trackSelectionOption.title}
            image={trackSelectionOption.trackBanner}
            videoBannerUrl={metaObjectTrackSelectionConfig?.virtualReality?.review_video_url}
          />
          <div className="tw-text-white">
            <div className="tw-text-[20px] tw-font-[700] tw-block tw-mt-[10px] tw-capitalize tw-text-center lg:tw-text-left tw-text-white">Virtual Reality</div>
            <div className="tw-flex tw-justify-between tw-mt-[10px] tw-text-white tw-leading-[0]">
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px] tw-capitalize">Players:</span>
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px] tw-capitalize" id="mcp-variant-quantity">
                {numberOfPlayers}
              </span>
            </div>
            <div className="tw-flex tw-justify-between lg:tw-gap-[20px] tw-mt-[10px] tw-text-white tw-leading-[1]">
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px]">Number of Sessions:</span>
              <span className="tw-text-[16px] tw-font-[600] tw-flex tw-justify-between tw-mt-[10px] tw-capitalize">{numberOfSessions}</span>
            </div>
            <div className="tw-text-white">
              {uniqueVariants.length &&
                uniqueVariants.map((variant) => {
                  return (
                    <div
                      key={variant.variantSelectedTime}
                      id="mcp-product-id-selector"
                      data-product-id={getAdminIdFromStoreFrontId(variant.uniqueVariants[0].product.id as string)}
                    >
                      <span className="tw-text-[18px] tw-font-[700] tw-block tw-my-[10px] tw-text-center lg:tw-text-left">
                        {formatDateWithSuffix(new Date(variant.variantSelectedTime))}
                      </span>
                      <div className="tw-grid tw-grid-cols-3 tw-gap-[20px] tw-mt-[10px]">
                        {variant.uniqueVariants &&
                          variant.uniqueVariants.map((xv) => {
                            const gameTitle = xv.selectedOptions.find((option) => option.name.includes("Time"));
                            const gameName = xv.selectedOptions.find((option) => option.name.includes("Game"));
                            return (
                              <SampleVariantsBlock
                                isGameVR={true}
                                variantId={xv.id}
                                key={xv.id}
                                title={gameTitle?.value as string}
                                price={xv.priceV2.amount}
                                vrGameName={gameName?.value as TGameOptions}
                              />
                            );
                          })}
                      </div>
                    </div>
                  );
                })}

              <span className="tw-text-[18px] tw-font-[600] tw-block tw-mt-[10px] tw-text-center lg:tw-text-left">Total</span>
              <span className="tw-text-[30px] tw-font-[700] tw-leading-[1] tw-mt-[10px] tw-block tw-text-center lg:tw-text-left">
                $<span>{Number(totalPrice * numberOfPlayers).toFixed(2)}</span>
              </span>
              <span className="tw-text-[12px] md:tw-text-[14px] tw-font-[500] tw-block tw-my-[20px] tw-text-center lg:tw-text-left tw-flex tw-items-center tw-justify-center">
                Get <span className="tw-font-[700] tw-px-1">$19 off</span> with Hyper Karting{" "}
                <a href="https://hyperkarting.com.au/products/membership">
                  <img
                    src="https://cdn.shopify.com/s/files/1/0496/8733/0976/files/HK-Membership_Icon_a819308e-9913-473d-b1ac-b5aa3d061947.png?v=1695350485"
                    alt="membership logo"
                    style={{
                      maxWidth: "150px",
                      width: "100%",
                    }}
                  />
                </a>
              </span>
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-[20px]">
                {isTermsAndConditionExcepted ? (
                  <>
                    {productRenderedLocation === "external" ? (
                      <>
                        <span
                          onClick={() => handleAddToCartClick()}
                          className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white lg:tw-max-w-[230px] lg:tw-w-full tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] bg-gradient-2 tw-rounded-full tw-cursor-pointer`}
                          id="add-to-cart-button"
                        >
                          <>
                            {isProductAdding ? (
                              <>
                                <ImSpinner6 className="tw-w-[26px] tw-h-[26px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                              </>
                            ) : (
                              <>
                                <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Add to Cart</span>
                              </>
                            )}
                          </>
                        </span>
                        <span
                          onClick={() => handleBookNow()}
                          className="tw-flex tw-items-center tw-justify-center tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] lg:tw-max-w-[230px] lg:tw-w-full tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                        >
                          {isBookingNow ? "Booking..." : "Book Now"}
                        </span>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleAddToCartClick()}
                          className={`tw-flex tw-items-center tw-justify-center tw-text-center tw-text-white lg:tw-max-w-[230px] lg:tw-w-full tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] bg-gradient-2 tw-rounded-full tw-cursor-pointer`}
                          id="add-to-cart-button"
                        >
                          <>
                            {isProductAdding ? (
                              <>
                                <ImSpinner6 className="tw-w-[26px] tw-h-[26px] tw-animate-spin tw-transition tw-duration-300 tw-ease-in-out" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Adding...</span>
                              </>
                            ) : (
                              <>
                                <ShoppingCartIcon className="tw-w-[26px] tw-h-[26px]" />
                                <span className="tw-font-extrabold tw-text-[medium] tw-ml-[10px]">Add to Cart</span>
                              </>
                            )}
                          </>
                        </button>
                        <button
                          onClick={() => handleBookNow()}
                          className="tw-flex tw-items-center tw-justify-center tw-text-center tw-font-extrabold tw-text-[medium] tw-text-[#9D62FE] tw-py-[16px] tw-px-[40px] lg:tw-p-[14px] lg:tw-max-w-[230px] lg:tw-w-full tw-border-[#9D62FE] tw-border tw-rounded-full tw-cursor-pointer"
                        >
                          {isBookingNow ? "Booking..." : "Book Now"}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]`}>
                      <span className={`tw-text-[red] tw-font-[700] tw-text-center tw-flex`}>Please accept Terms & Conditions to continue</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px] tw-mt-[40px] lg:tw-mt-0">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div className="tw-block lg:tw-hidden">
            <Navigation
              previous={{
                active: true,
                to: "/" + ROUTES.VIRTUAL_REALITY.DATE_TIME,
              }}
              next={{ active: nextRoute !== "#", to: nextRoute }}
              title={"Review your Experience"}
              subtitle={"Virtual Reality"}
            />
          </div>
          <div
            className={`${
              productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"
            } tw-relative tw-flex tw-justify-end tw-items-center tw-gap-[15px] lg:tw-block`}
          >
          </div>
        </div>
      </div>
    </>
  );
};

export default VRReviewExperience;
